import "@/App.css";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import {
  getStaffPortalStart,
  markStaffPortalFromPath,
  shouldRedirectAwayFromManager,
} from "@/lib/staffPortal";
import { setManagerReturnToFromLocation } from "@/lib/authSession";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import OrderNotificationProvider from "@/components/OrderNotificationProvider";
import SensitivePageGate from "@/components/SensitivePageGate";
import { warmCashfreeSdk } from "@/lib/cashfreeLoader";

const DashboardRoot = lazy(() => import("@/features/dashboard/DashboardV2Root"));
const AnalyticsRoot = lazy(() => import("@/features/analytics/AnalyticsV2Root"));
const AppAnalyticsRoot = lazy(() => import("@/features/app-analytics/AppAnalyticsV2Root"));
const DailyReportRoot = lazy(() => import("@/features/daily-report/DailyReportV2Root"));
const WeAgentReportRoot = lazy(() => import("@/features/we-agent-report/WeAgentReportV2Root"));
const MarketingRoot = lazy(() => import("@/features/marketing/MarketingV2Root"));
const InventoryRoot = lazy(() => import("@/features/inventory/InventoryV2Root"));
const BakingRoot = lazy(() => import("@/features/recipes/BakingV2Root"));
const CatalogRoot = lazy(() => import("@/features/catalog/CatalogV2Root"));
const CustomersRoot = lazy(() => import("@/features/customers/CustomersV2Root"));
const TeamRoot = lazy(() => import("@/features/team/TeamV2Root"));
const TasksRoot = lazy(() => import("@/features/tasks/TasksV2Root"));
const SettingsRoot = lazy(() => import("@/features/settings/SettingsV2Root"));
const CalendarRoot = lazy(() => import("@/features/calendar/CalendarV2Root"));
const AiAssistantRoot = lazy(() => import("@/features/ai-assistant/AiAssistantV2Root"));
const ChatRoot = lazy(() => import("@/features/chat/ChatV2Root"));
const OrdersRoot = lazy(() => import("@/features/orders/OrdersV2Root"));
const OrderDetailPage = lazy(() => import("@/features/orders/OrderDetail.v2"));
const NewOrderRoot = lazy(() => import("@/features/orders/NewOrderV2Root"));
const EditOrderPage = lazy(() => import("@/features/orders/EditOrder.v2"));

const DriverHandoff = lazy(() => import("@/pages/DriverHandoff"));
const DummyCodQrDemo = lazy(() => import("@/pages/DummyCodQrDemo"));
const ProductionHub = lazy(() => import("@/pages/ProductionHub"));
const InventoryHub = lazy(() => import("@/pages/InventoryHub"));
const CaHub = lazy(() => import("@/pages/CaHub"));
const TcHub = lazy(() => import("@/pages/TcHub"));
const WeHub = lazy(() => import("@/pages/WeHub"));
const SaHub = lazy(() => import("@/pages/SaHub"));
const DesignGalleryHub = lazy(() => import("@/pages/DesignGalleryHub"));

import DriverRedirect from "@/pages/DriverRedirect";
import DriverDashboardRedirect from "@/pages/DriverDashboardRedirect";
import KitchenStaffJoin from "@/pages/KitchenStaffJoin";
import DriverPartnerJoin from "@/pages/DriverPartnerJoin";
import DriverPartnerScan from "@/pages/DriverPartnerScan";
import ProductionDashboardRedirect from "@/pages/ProductionDashboardRedirect";
import ProductionUnitScanRedirect from "@/pages/ProductionUnitScanRedirect";
import ProductionRedirect from "@/pages/ProductionRedirect";
import BakerTask from "@/pages/BakerTask";
import FrosterTask from "@/pages/FrosterTask";
import BakerCalculator from "@/pages/BakerCalculator";
import FrosterCalculator from "@/pages/FrosterCalculator";
import DecoratorTask from "@/pages/DecoratorTask";
import CakeProofPage from "@/pages/CakeProofPage";
import FrostingProofPage from "@/pages/FrostingProofPage";
import FreshnessStampPage from "@/pages/FreshnessStampPage";
import InventoryStaffJoin from "@/pages/InventoryStaffJoin";
import InventoryDashboardRedirect from "@/pages/InventoryDashboardRedirect";
import InventoryLabelsPrintPage from "@/pages/InventoryLabelsPrintPage";
import InventoryScanRedirect from "@/pages/InventoryScanRedirect";
import ManageLogin from "@/pages/ManageLogin";
import TeamHubRoutes from "@/pages/TeamHubRoutes";
import TsPortionGate from "@/components/mobile/TsPortionGate";
import TsAppShell from "@/components/mobile/TsAppShell";
import { isTeamHubHost, teamHubUrl, teamPathFromLegacyTs } from "@/lib/teamHub";
import { OPS_BASENAME, OPS_MOUNTED } from "@/lib/opsBase";

function RouteFallback() {
  return (
    <div className="flex items-center justify-center py-20">
      <div className="spinner" />
    </div>
  );
}

function LazyRoute({ children }) {
  return <Suspense fallback={<RouteFallback />}>{children}</Suspense>;
}

function ProtectedRoute({ children }) {
  const { isAuthenticated } = useAuth();
  const { pathname, search, hash } = useLocation();

  useEffect(() => {
    if (!isAuthenticated && !shouldRedirectAwayFromManager(pathname)) {
      setManagerReturnToFromLocation({ pathname, search, hash });
    }
  }, [isAuthenticated, pathname, search, hash]);

  if (!isAuthenticated) {
    if (shouldRedirectAwayFromManager(pathname)) {
      const start = getStaffPortalStart();
      if (start && start.startsWith("http")) {
        window.location.replace(start);
        return null;
      }
      if (start) return <Navigate to={start} replace />;
    }
    return <Navigate to="/login" replace />;
  }
  return children;
}

function StaffPortalRouteGuard({ children }) {
  const { pathname } = useLocation();
  markStaffPortalFromPath(pathname);
  useEffect(() => {
    markStaffPortalFromPath(pathname);
  }, [pathname]);
  if (shouldRedirectAwayFromManager(pathname)) {
    const start = getStaffPortalStart();
    if (start && start.startsWith("http")) {
      window.location.replace(start);
      return null;
    }
    if (start) return <Navigate to={start} replace />;
  }
  return children;
}

function LoginRoute() {
  const { isAuthenticated } = useAuth();
  if (isAuthenticated) return <Navigate to="/" replace />;
  if (shouldRedirectAwayFromManager("/login")) {
    const start = getStaffPortalStart();
    if (start && start.startsWith("http")) {
      window.location.replace(start);
      return null;
    }
    if (start) return <Navigate to={start} replace />;
  }
  // Phone + OTP, the same identity as the Team hub. The shared password lives
  // on at /login?password=1 as the documented rollback (ManageLogin).
  return <ManageLogin />;
}

function LegacyKitchenRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/pp/hub${search}`} replace />;
}

function LegacyDriverRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/dd/hub${search}`} replace />;
}

function StaffPortalRouteFallback() {
  return (
    <div className="flex items-center justify-center py-20 min-h-[100dvh]" style={{ background: "#F5F2EA" }}>
      <div className="spinner" />
    </div>
  );
}

function LegacyTbRedirect() {
  const { pathname, search } = useLocation();
  return <Navigate to={`${pathname.replace(/^\/tb/, "/ts")}${search}`} replace />;
}

/**
 * Hand a legacy /ts path on the Manage host over to the Team hub.
 *
 * A full page load rather than a router Navigate, because it crosses origins.
 * The TS session lives in localStorage, which is per-origin, so anyone still
 * signed in here signs in once more on Team. That is the unavoidable cost of
 * moving the front door, and it is paid once per person.
 */
function LegacyTsToTeamRedirect() {
  const { pathname, search } = useLocation();
  useEffect(() => {
    window.location.replace(`${teamHubUrl(teamPathFromLegacyTs(pathname))}${search || ""}`);
  }, [pathname, search]);
  return <StaffPortalRouteFallback />;
}

function AppRoutes() {
  // team.thetabletop.in is a dedicated staff front door (login-or-home at `/`),
  // except under /ops, where it serves the dashboard below with `/ops` as the
  // router basename. Both trees ship in one bundle from one docroot.
  if (typeof window !== "undefined" && isTeamHubHost() && !OPS_MOUNTED) {
    return <TeamHubRoutes />;
  }

  return (
    <StaffPortalRouteGuard>
      <Routes>
        <Route path="/tb" element={<Navigate to="/ts" replace />} />
        <Route path="/tb/*" element={<LegacyTbRedirect />} />
        {/* team.thetabletop.in is the staff front door now. These paths stay
            registered so existing bookmarks, QR codes and the installed TS PWA
            land somewhere correct instead of 404ing — they just hand off to
            Team. Legacy /tb keeps redirecting through /ts to here. */}
        <Route path="/ts" element={<LegacyTsToTeamRedirect />} />
        <Route path="/ts/*" element={<LegacyTsToTeamRedirect />} />
        <Route path="/pp/hub" element={<TsPortionGate portion="kitchen"><Suspense fallback={<StaffPortalRouteFallback />}><ProductionHub /></Suspense></TsPortionGate>} />
        <Route path="/pp/join" element={<KitchenStaffJoin />} />
        <Route path="/ka/t/:code" element={<ProductionUnitScanRedirect />} />
        <Route path="/ka/u/:code" element={<ProductionUnitScanRedirect />} />
        <Route path="/pp/:code" element={<ProductionDashboardRedirect />} />
        <Route path="/dd/hub" element={<Suspense fallback={<StaffPortalRouteFallback />}><DriverHandoff /></Suspense>} />
        <Route path="/dd/scan" element={<DriverPartnerScan />} />
        <Route path="/dd/join" element={<DriverPartnerJoin />} />
        <Route path="/dd/dummy-qr" element={<Suspense fallback={<StaffPortalRouteFallback />}><DummyCodQrDemo /></Suspense>} />
        <Route path="/dd/:code" element={<DriverDashboardRedirect />} />
        <Route path="/ti/hub" element={<TsPortionGate portion="inventory"><Suspense fallback={<StaffPortalRouteFallback />}><InventoryHub /></Suspense></TsPortionGate>} />
        <Route path="/ti/join" element={<InventoryStaffJoin />} />
        <Route path="/ti/labels" element={<InventoryLabelsPrintPage />} />
        <Route path="/ti/s/:code" element={<InventoryScanRedirect />} />
        <Route path="/ti/:code" element={<InventoryDashboardRedirect />} />
        <Route path="/ca/hub" element={<TsPortionGate portion="chat"><Suspense fallback={<StaffPortalRouteFallback />}><CaHub /></Suspense></TsPortionGate>} />
        <Route path="/sa/hub" element={<TsPortionGate portion="salary"><Suspense fallback={<StaffPortalRouteFallback />}><SaHub /></Suspense></TsPortionGate>} />
        <Route path="/we/hub" element={<TsPortionGate portion="we"><Suspense fallback={<StaffPortalRouteFallback />}><WeHub /></Suspense></TsPortionGate>} />
        <Route path="/kitchen" element={<LegacyKitchenRedirect />} />
        <Route path="/driver" element={<LegacyDriverRedirect />} />
        <Route path="/d/:code" element={<DriverRedirect />} />
        <Route path="/p/cake-proof/:code" element={<CakeProofPage />} />
        <Route path="/p/frosting-proof/:code" element={<FrostingProofPage />} />
        <Route path="/p/:role/:code" element={<ProductionRedirect />} />
        <Route path="/production/baker" element={<BakerTask />} />
        <Route path="/production/froster" element={<FrosterTask />} />
        <Route path="/calculator/baker" element={<BakerCalculator />} />
        <Route path="/calculator/froster" element={<FrosterCalculator />} />
        <Route path="/production/decorator" element={<DecoratorTask />} />
        <Route path="/login" element={<LoginRoute />} />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <OrderNotificationProvider>
                <Layout />
              </OrderNotificationProvider>
            </ProtectedRoute>
          }
        >
          <Route index element={<LazyRoute><DashboardRoot /></LazyRoute>} />
          <Route path="orders" element={<LazyRoute><OrdersRoot /></LazyRoute>} />
          <Route path="orders/priority-today" element={<LazyRoute><OrdersRoot priorityOnly /></LazyRoute>} />
          <Route path="orders/new" element={<LazyRoute><NewOrderRoot /></LazyRoute>} />
          <Route path="orders/:id/stamp" element={<FreshnessStampPage />} />
          <Route path="orders/:id" element={<LazyRoute><OrderDetailPage /></LazyRoute>} />
          <Route path="orders/:id/edit" element={<LazyRoute><EditOrderPage /></LazyRoute>} />
          <Route
            path="analytics"
            element={
              <SensitivePageGate title="Analytics">
                <LazyRoute><AnalyticsRoot /></LazyRoute>
              </SensitivePageGate>
            }
          />
          <Route path="app-analytics" element={<LazyRoute><AppAnalyticsRoot /></LazyRoute>} />
          <Route path="marketing" element={<LazyRoute><MarketingRoot /></LazyRoute>} />
          <Route path="inventory" element={<LazyRoute><InventoryRoot /></LazyRoute>} />
          <Route path="baking" element={<LazyRoute><BakingRoot /></LazyRoute>} />
          <Route path="catalog" element={<LazyRoute><CatalogRoot /></LazyRoute>} />
          <Route path="customers" element={<LazyRoute><CustomersRoot /></LazyRoute>} />
          <Route path="customers/existing" element={<LazyRoute><CustomersRoot /></LazyRoute>} />
          <Route path="customers/leads" element={<LazyRoute><CustomersRoot /></LazyRoute>} />
          <Route path="customers/leads/stat/:statKey" element={<LazyRoute><CustomersRoot /></LazyRoute>} />
          <Route path="customers/leads/occasion/:occasionKey" element={<LazyRoute><CustomersRoot /></LazyRoute>} />
          <Route path="customers/leads/:leadId" element={<LazyRoute><CustomersRoot /></LazyRoute>} />
          <Route path="customers/topins" element={<LazyRoute><CustomersRoot /></LazyRoute>} />
          <Route path="team" element={<LazyRoute><TeamRoot /></LazyRoute>} />
          <Route path="tasks" element={<LazyRoute><TasksRoot /></LazyRoute>} />
          <Route path="notes" element={<Navigate to="/tasks" replace />} />
          <Route path="ai-assistant" element={<LazyRoute><AiAssistantRoot /></LazyRoute>} />
          <Route path="tc" element={<LazyRoute><ChatRoot /></LazyRoute>} />
          <Route path="settings" element={<LazyRoute><SettingsRoot /></LazyRoute>} />
          <Route path="calendar" element={<LazyRoute><CalendarRoot /></LazyRoute>} />
          <Route
            path="daily-report"
            element={
              <SensitivePageGate title="Daily Report">
                <LazyRoute><DailyReportRoot /></LazyRoute>
              </SensitivePageGate>
            }
          />
          <Route path="we-agent-report" element={<LazyRoute><WeAgentReportRoot /></LazyRoute>} />
        </Route>
      </Routes>
    </StaffPortalRouteGuard>
  );
}

function App() {
  useEffect(() => {
    warmCashfreeSdk();
  }, []);

  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter basename={OPS_MOUNTED ? OPS_BASENAME : undefined}>
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
